import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export type InstitutionConfiguredGroupRow = { id: string; name: string; membersCount: number };

/** Chaîne « Groupe » API (noms séparés par des virgules) → liste pour surbrillance dans le catalogue. */
export function splitInstitutionLearnerGroupLabels(raw: string | null | undefined): string[] {
  if (!raw || raw.trim() === '' || raw.trim() === '—') return [];
  return raw.split(/\s*,\s*/).map((s) => s.trim()).filter(Boolean);
}

/** Réponse `GET /institution/groups` (successResponse + formes enveloppées). */
export function parseInstitutionGroupsApiResponse(raw: unknown): InstitutionConfiguredGroupRow[] {
  const unwrap = (r: unknown): unknown => {
    if (r && typeof r === 'object' && 'data' in r) {
      const inner = (r as { data: unknown }).data;
      if (inner && typeof inner === 'object' && inner !== null && 'data' in inner) {
        return (inner as { data: unknown }).data;
      }
      return inner;
    }
    return r;
  };
  const root = unwrap(raw);
  const arr = Array.isArray(root)
    ? root
    : root && typeof root === 'object' && 'data' in root && Array.isArray((root as { data: unknown[] }).data)
      ? (root as { data: unknown[] }).data
      : [];
  return arr
    .map((x) => {
      if (!x || typeof x !== 'object') return null;
      const o = x as Record<string, unknown>;
      const id = o.id != null ? String(o.id) : '';
      const name = o.name != null ? String(o.name) : '';
      const membersCount = typeof o.membersCount === 'number' ? o.membersCount : 0;
      if (!id || !name) return null;
      return { id, name, membersCount };
    })
    .filter((x): x is InstitutionConfiguredGroupRow => x != null);
}

type InstitutionConfiguredGroupsListProps = {
  groups: InstitutionConfiguredGroupRow[];
  className?: string;
  /** Noms des groupes dont la personne est membre (champ « groupe » côté API, séparé par virgules). */
  currentGroupNames?: string[];
  /** Édition : sélection par ID (`learningGroupIds` API). */
  interactive?: boolean;
  selectedGroupIds?: string[];
  onSelectedGroupIdsChange?: (ids: string[]) => void;
};

export function InstitutionConfiguredGroupsList({
  groups,
  className,
  currentGroupNames,
  interactive = false,
  selectedGroupIds = [],
  onSelectedGroupIdsChange,
}: InstitutionConfiguredGroupsListProps) {
  const { t } = useTranslation();
  const inSet = new Set(
    (currentGroupNames ?? [])
      .map((s) => s.trim().toLowerCase())
      .filter((s) => s.length > 0 && s !== '—'),
  );
  const idSet = useMemo(
    () => new Set((selectedGroupIds ?? []).map((id) => id.trim()).filter(Boolean)),
    [selectedGroupIds],
  );

  const toggleId = (id: string, checked: boolean) => {
    if (!onSelectedGroupIdsChange) return;
    const next = new Set(idSet);
    if (checked) next.add(id);
    else next.delete(id);
    onSelectedGroupIdsChange([...next]);
  };

  if (groups.length === 0) {
    return (
      <p className={cn('text-xs text-[#5B6472] dark:text-[#B6CAEC]', className)}>
        {t('institution.groups.emptyNone')}
      </p>
    );
  }

  return (
    <ul
      className={cn(
        'relative z-10 max-h-48 space-y-1 overflow-y-auto overscroll-contain',
        interactive && 'touch-pan-y',
        className,
      )}
      role="list"
    >
      {groups.map((g) => {
        const active = interactive ? idSet.has(g.id) : inSet.has(g.name.trim().toLowerCase());
        return (
          <li
            key={g.id}
            className={cn(
              'flex min-h-[2.75rem] items-center justify-between gap-2 rounded-md border px-2 py-2 text-xs',
              active
                ? 'border-primary-200 bg-primary-50/90 text-[#0C1C38] dark:border-primary-800 dark:bg-primary-950/40 dark:text-white'
                : 'border-[#F0F4FA] bg-white/90 text-[#121826] dark:border-[#19335E] dark:bg-[#0C1C38]/50 dark:text-[#DCE7F7]',
              interactive && 'cursor-pointer select-none',
            )}
            onClick={
              interactive
                ? (e) => {
                    const el = e.target as HTMLElement;
                    if (el.closest('input[type="checkbox"]')) return;
                    toggleId(g.id, !idSet.has(g.id));
                  }
                : undefined
            }
          >
            <span className="flex min-w-0 flex-1 items-center gap-3">
              {interactive ? (
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 cursor-pointer rounded border-[#E8EDF5] text-primary-600 focus:ring-2 focus:ring-primary-500 dark:border-[#B6CAEC] dark:bg-[#122644]"
                  checked={active}
                  onChange={(e) => {
                    e.stopPropagation();
                    toggleId(g.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  aria-label={g.name}
                />
              ) : null}
              <span className="min-w-0 truncate font-medium">{g.name}</span>
            </span>
            <span className="shrink-0 text-[#5B6472] dark:text-[#B6CAEC]">
              {t('institution.groups.memberCount', { count: g.membersCount })}
            </span>
          </li>
        );
      })}
    </ul>
  );
}
